import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { devices, metrics, type DeviceDetail, type DeviceInterface, csvUrls } from "../lib/api.js";
import { displayStatus, relativeTime } from "../lib/freshness.js";
import { psuDisplay } from "../lib/psu.js";
import { useLiveData } from "../lib/use-live-data.js";
import { useNow } from "../lib/use-now.js";
import { useMediaQuery } from "../lib/use-media-query.js";
import { splitInterfaces } from "../lib/interfaces.js";
import { totalsLabel } from "../lib/traffic-totals.js";
import { useSeries } from "../lib/use-series.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import { CONTROL_BUTTON, CONTROL_ROW } from "../components/control-style.js";
import { useMainWidth } from "../components/AppShell.js";
import { CustomMetricsSection } from "../components/CustomMetricsSection.js";
import { DeviceChat } from "../components/DeviceChat.js";
import { DeviceLogsSection } from "../components/DeviceLogsSection.js";
import { MaintenanceControl } from "../components/MaintenanceControl.js";
import { ExportCsv } from "../components/ExportCsv.js";
import { LineChart, type ChartSeries } from "../components/LineChart.js";
import { toChartNumber } from "../lib/chart-number.js";
import { RangePicker } from "../components/RangePicker.js";
import { SharePanel } from "../components/SharePanel.js";
import { StatusBadge } from "../components/StatusBadge.js";

/** Matches the device list's cadence, for the same reasons. */
const DETAIL_REFRESH_MS = 30_000;

const CHARGE_STATE_LABELS = [
  "Start", "Night Check", "Disconnect", "Night", "Fault",
  "MPPT", "Absorption", "Float", "Equalize", "Slave",
];

const bitsPerSecond = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} Mbit/s`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)} kbit/s`;
  return `${v.toFixed(0)} bit/s`;
};
/**
 * Axis variant: same units, fewer digits. A tooltip is read one value at a
 * time and wants precision; an axis is read as a scale and wants brevity.
 */
const bitsPerSecondAxis = (v: number) => {
  if (Math.abs(v) >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)} Gb/s`;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(0)} Mb/s`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)} kb/s`;
  return `${v.toFixed(0)}`;
};

const volts = (v: number) => `${v.toFixed(2)} V`;
const amps = (v: number) => `${v.toFixed(2)} A`;
const watts = (v: number) => `${v.toFixed(0)} W`;
const celsius = (v: number) => `${v.toFixed(1)} °C`;
const percent = (v: number) => `${(v * 100).toFixed(0)}%`;

function InterfaceSection({
  iface,
  onToggleHidden,
}: {
  iface: DeviceInterface;
  /** Present only for someone who may edit the device. */
  onToggleHidden?: () => void;
}) {
  const [hours, setHours] = useState(24);
  const { data, loading } = useSeries((r) => metrics.interface(iface.id, r), hours, [iface.id]);
  const totals = data ? totalsLabel(data.totals) : null;

  const series: ChartSeries[] = [
    {
      key: "in",
      label: "Inbound",
      color: "var(--series-1)",
      format: bitsPerSecond,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.in_bps) })),
    },
    {
      key: "out",
      label: "Outbound",
      color: "var(--series-2)",
      format: bitsPerSecond,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.out_bps) })),
    },
  ];

  return (
    <section style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <h2 style={sectionTitle}>
            {iface.if_name ?? `Interface ${iface.if_index}`}
            {iface.if_alias && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> — {iface.if_alias}</span>}
          </h2>
          {/* Under the title rather than beside the controls: the header row
              already has to fit the range picker and CSV button, and wraps
              badly on a narrow screen when anything else joins it. */}
          {totals && (
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 4, letterSpacing: "0.06em" }}>
              {totals.text}
              {totals.partial && <span style={{ color: "var(--text-muted)" }}> ({totals.partial})</span>}
            </div>
          )}
        </div>
        <div style={CONTROL_ROW}>
          <RangePicker hours={hours} onChange={setHours} />
          <ExportCsv hours={hours} url={(r) => csvUrls.interface(iface.id, r)} />
          {onToggleHidden && (
            <button
              onClick={onToggleHidden}
              title={iface.hidden ? "Show this interface's charts again" : "Hide this interface's charts for everyone. It is still collected."}
              style={{ ...CONTROL_BUTTON, border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)" }}
            >
              {iface.hidden ? "[ UNHIDE ]" : "[ HIDE ]"}
            </button>
          )}
        </div>
      </div>
      <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity 150ms" }}>
        <LineChart
          series={series}
          yLabel="Bandwidth"
          yTickFormat={bitsPerSecondAxis}
          resolutionNote={data?.resolution}
        />
      </div>
    </section>
  );
}

function RouterDeviceSection({ deviceId, reports }: { deviceId: string; reports: DeviceDetail["reports"] }) {
  const [hours, setHours] = useState(24);
  const { data, loading } = useSeries((r) => metrics.device(deviceId, r), hours, [deviceId]);

  const series: ChartSeries[] = [
    {
      key: "cpu",
      label: "CPU load",
      color: "var(--series-1)",
      format: (v) => `${v.toFixed(0)}%`,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.cpu_load) })),
    },
  ];

  const tempSeries: ChartSeries[] = [
    {
      key: "temp",
      label: "Board temperature",
      color: "var(--series-5)",
      format: celsius,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.temperature_c) })),
    },
  ];

  return (
    <section style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={sectionTitle}>&gt; DEVICE HEALTH</h2>
        <div style={CONTROL_ROW}>
          <RangePicker hours={hours} onChange={setHours} />
          <ExportCsv hours={hours} url={(r) => csvUrls.device(deviceId, r)} />
        </div>
      </div>
      <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity 150ms", display: "grid", gap: 20 }}>
        {reports.cpu && (
          <div>
            <div style={subLabel}>CPU load</div>
            <LineChart series={series} zeroBaseline />
          </div>
        )}
        {reports.temperature && (
          <div>
            <div style={subLabel}>Temperature</div>
            <LineChart series={tempSeries} zeroBaseline={false} />
          </div>
        )}
      </div>
    </section>
  );
}

function ChargeControllerSection({ deviceId }: { deviceId: string }) {
  const [hours, setHours] = useState(24);
  const { data, loading } = useSeries(
    (r) => metrics.power(deviceId, { ...r, channel: 0 }),
    hours,
    [deviceId],
  );
  const latest = data?.points.at(-1);

  const powerSeries: ChartSeries[] = [
    {
      key: "battery",
      label: "Battery voltage",
      color: "var(--series-1)",
      format: volts,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.voltage_v) })),
    },
    {
      key: "array",
      label: "Array voltage",
      color: "var(--series-4)",
      format: volts,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.array_voltage_v) })),
    },
  ];

  const currentSeries: ChartSeries[] = [
    {
      key: "charge",
      label: "Charge current",
      color: "var(--series-3)",
      format: amps,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.current_a) })),
    },
  ];

  const powerWSeries: ChartSeries[] = [
    {
      key: "power",
      label: "Output power",
      color: "var(--series-2)",
      format: watts,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.power_w) })),
    },
  ];

  const tempSeries: ChartSeries[] = [
    {
      key: "battery-temp",
      label: "Battery",
      color: "var(--series-1)",
      format: celsius,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.battery_temp_c) })),
    },
    {
      key: "heatsink-temp",
      label: "Heatsink",
      color: "var(--series-5)",
      format: celsius,
      points: (data?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.heatsink_temp_c) })),
    },
  ];

  return (
    <section style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={sectionTitle}>&gt; CHARGE CONTROLLER</h2>
        <div style={CONTROL_ROW}>
          <RangePicker hours={hours} onChange={setHours} />
          <ExportCsv hours={hours} url={(r) => csvUrls.power(deviceId, { ...r, channel: 0 })} />
        </div>
      </div>

      {latest && (
        <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
          <StatTile label="Battery" value={latest.voltage_v !== null ? volts(latest.voltage_v) : "—"} />
          <StatTile label="Charging" value={latest.current_a !== null ? amps(latest.current_a) : "—"} />
          <StatTile label="Output" value={latest.power_w !== null ? watts(latest.power_w) : "—"} />
          <StatTile
            label="State"
            value={latest.charge_state !== null ? CHARGE_STATE_LABELS[latest.charge_state] ?? String(latest.charge_state) : "—"}
          />
          <StatTile label="Lifetime" value={latest.kwh_total !== null ? `${latest.kwh_total.toFixed(0)} kWh` : "—"} />
        </div>
      )}

      <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity 150ms", display: "grid", gap: 20 }}>
        <div>
          <div style={subLabel}>Voltage</div>
          <LineChart series={powerSeries} zeroBaseline={false} />
        </div>
        <div>
          <div style={subLabel}>Charge current</div>
          <LineChart series={currentSeries} />
        </div>
        <div>
          <div style={subLabel}>Output power</div>
          <LineChart series={powerWSeries} />
        </div>
        <div>
          <div style={subLabel}>Temperature</div>
          <LineChart series={tempSeries} zeroBaseline={false} />
        </div>
      </div>
    </section>
  );
}

function DcMonitorSection({ deviceId }: { deviceId: string }) {
  const [hours, setHours] = useState(24);
  const { data: channel1, loading: l1 } = useSeries(
    (r) => metrics.power(deviceId, { ...r, channel: 1 }),
    hours,
    [deviceId],
  );
  const { data: channel0, loading: l0 } = useSeries(
    (r) => metrics.power(deviceId, { ...r, channel: 0 }),
    hours,
    [deviceId],
  );
  const { data: relays, loading: lr } = useSeries(
    (r) => metrics.relays(deviceId, r),
    hours,
    [deviceId],
  );

  const batterySeries: ChartSeries[] = [
    {
      key: "battery",
      label: "Battery voltage",
      color: "var(--series-1)",
      format: volts,
      points: (channel1?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.voltage_v) })),
    },
  ];

  const tempSeries: ChartSeries[] = [
    {
      key: "enclosure",
      label: "Enclosure",
      color: "var(--series-2)",
      format: celsius,
      points: (channel0?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.temperature_c) })),
    },
    {
      key: "board",
      label: "Monitor board (self-heating)",
      color: "var(--series-6)",
      format: celsius,
      points: (channel0?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.temperature2_c) })),
    },
  ];

  const relayIndices = [...new Set((relays?.points ?? []).map((p) => p.relay_index))].sort();
  const relayColors = ["var(--series-6)", "var(--series-5)", "var(--series-3)", "var(--series-4)"];
  const relaySeries: ChartSeries[] = relayIndices.map((idx, i) => ({
    key: `relay-${idx}`,
    label: `Relay ${idx} duty cycle`,
    color: relayColors[i % relayColors.length]!,
    format: percent,
    points: (relays?.points ?? [])
      .filter((p) => p.relay_index === idx)
      .map((p) => ({ x: new Date(p.t).getTime(), y: toChartNumber(p.duty_cycle) })),
  }));

  return (
    <section style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={sectionTitle}>&gt; DC MONITOR</h2>
        <div style={CONTROL_ROW}>
          <RangePicker hours={hours} onChange={setHours} />
          <ExportCsv hours={hours} url={(r) => csvUrls.power(deviceId, { ...r, channel: 1 })} />
        </div>
      </div>
      <div style={{ opacity: l1 || l0 || lr ? 0.6 : 1, transition: "opacity 150ms", display: "grid", gap: 20 }}>
        <div>
          <div style={subLabel}>Battery voltage</div>
          <LineChart series={batterySeries} zeroBaseline={false} />
        </div>
        <div>
          <div style={subLabel}>Temperature — enclosure vs. monitor's own board</div>
          <LineChart series={tempSeries} zeroBaseline={false} />
        </div>
        {relaySeries.length > 0 && (
          <div>
            <div style={subLabel}>Relay duty cycle</div>
            <LineChart series={relaySeries} zeroBaseline />
          </div>
        )}
      </div>
    </section>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ border: "1px solid var(--border)", padding: "8px 12px", minWidth: 104 }}>
      <div
        style={{
          fontSize: 10,
          color: "var(--text-muted)",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          color: "var(--text-primary)",
          textShadow: "0 0 4px currentColor",
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Whether the discussion sidebar is open, remembered per browser.
 *
 * Browser storage, not the account: it is a preference about this screen's
 * layout, the same person may want it open on a desktop and closed on a
 * laptop, and losing it costs one click. Wrapped because storage can be
 * unavailable or throw, and a layout preference must never break the page.
 */
const DISCUSSION_KEY = "rr.discussion.open";
function readDiscussionOpen(): boolean {
  try {
    return window.localStorage.getItem(DISCUSSION_KEY) !== "0";
  } catch {
    return true;
  }
}
function writeDiscussionOpen(open: boolean): void {
  try {
    window.localStorage.setItem(DISCUSSION_KEY, open ? "1" : "0");
  } catch {
    // Not remembered, which is fine.
  }
}

/** Wide enough to put the discussion beside the content rather than above it. */
const SIDEBAR_QUERY = "(min-width: 1180px)";
const SIDEBAR_WIDTH = 340;
const TAB_WIDTH = 34;

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const now = useNow();
  const wide = useMediaQuery(SIDEBAR_QUERY);
  const [discussionOpen, setDiscussionOpen] = useState(readDiscussionOpen);
  const [showHidden, setShowHidden] = useState(false);
  const [hideError, setHideError] = useState<string | null>(null);
  const toggleDiscussion = () => {
    setDiscussionOpen((was) => {
      writeDiscussionOpen(!was);
      return !was;
    });
  };
  // Widen the page by what the sidebar takes, so the charts keep the width
  // they had before it existed instead of being squeezed beside it.
  useMainWidth(wide ? 1100 + 16 + (discussionOpen ? SIDEBAR_WIDTH : TAB_WIDTH) : null);

  // The status block refreshes; the charts below do not. Re-running every
  // series query every 30s is real database work for graphs someone is in the
  // middle of reading, and a chart that redraws under the cursor is worse to
  // use than one that holds still. The range picker is how those refresh.
  const { data: detail, error, reload } = useLiveData<DeviceDetail>(
    () => (id ? devices.get(id) : Promise.reject(new Error("No device specified"))),
    DETAIL_REFRESH_MS,
    [id],
  );

  // Only a hard failure when there is nothing to show; a refresh that fails
  // over good data is reported inline below instead.
  if (error && !detail) return <p style={{ color: "var(--status-critical)" }}>{error}</p>;
  if (!detail || !id) return <p style={{ color: "var(--text-muted)" }}>LOADING...</p>;

  const { device, interfaces } = detail;
  const canWrite = detail.permissions.includes("write");
  const { shown: shownInterfaces, hidden: hiddenInterfaces } = splitInterfaces(interfaces);
  const toggleHidden = (iface: DeviceInterface) => async () => {
    setHideError(null);
    try {
      await devices.setInterfaceHidden(id, iface.id, !iface.hidden);
      reload();
    } catch (err) {
      setHideError(err instanceof Error ? err.message : "Could not change that");
    }
  };
  const interfaceChart = (iface: DeviceInterface) => (
    <ErrorBoundary key={iface.id} label={(iface.if_name ?? "INTERFACE").toUpperCase()}>
      <InterfaceSection iface={iface} onToggleHidden={canWrite ? toggleHidden(iface) : undefined} />
    </ErrorBoundary>
  );

  const discussion = (
    <DeviceChat
      deviceId={id}
      open={discussionOpen}
      onToggle={toggleDiscussion}
      mode={wide ? "column" : "stacked"}
    />
  );

  return (
    <div
      style={
        wide
          ? {
              display: "grid",
              gridTemplateColumns: `minmax(0, 1fr) ${discussionOpen ? SIDEBAR_WIDTH : TAB_WIDTH}px`,
              gap: 16,
              alignItems: "start",
            }
          : undefined
      }
    >
    <div style={{ minWidth: 0 }}>
      <Link to="/" style={{ fontSize: 13, color: "var(--text-secondary)", textDecoration: "none" }}>
        {"< DEVICES"}
      </Link>

      {/* Wraps as a whole rather than squeezing. The right-hand group grew --
          PSU badges, the maintenance picker -- and with the discussion sidebar
          open there is not always room for it beside the name, so the group
          drops below the name intact instead of every button folding its own
          label onto three lines. */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
          marginTop: 8,
          marginBottom: 20,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 20, margin: "0 0 4px" }}>{device.name}</h1>
          <div style={{ color: "var(--text-secondary)", fontSize: 13 }}>
            {device.address}
            {/* How it is reached, because a poll failure means something
                different depending on the answer: a device fault, or a
                connector that is down. */}
            {(device as { connector_name?: string | null }).connector_name && (
              <span style={{ color: "var(--text-muted)" }}>
                {` · via ${String((device as { connector_name?: string }).connector_name)}`}
                {(device as { connector_status?: string }).connector_status !== "online" && (
                  <span style={{ color: "var(--status-critical)" }}> (connector offline)</span>
                )}
              </span>
            )}
            {device.board_name && ` · ${device.board_name}`}
            {device.routeros_version && ` · RouterOS ${device.routeros_version}`}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", rowGap: 8 }}>
          {psuDisplay(device, now).map((psu) => (
            <span key={psu.label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, letterSpacing: "0.1em", color: "var(--text-muted)" }}>{psu.label}</span>
              <StatusBadge status={psu.status} />
            </span>
          ))}
          <span style={{ fontSize: 11, letterSpacing: "0.1em", color: "var(--text-muted)" }}>
            {`POLLED ${relativeTime(device.last_poll_at, now)}`}
          </span>
          <StatusBadge status={displayStatus(device, now)} />
          <MaintenanceControl
            variant="control"
            deviceId={id}
            device={device}
            canWrite={detail.permissions.includes("write")}
            now={now}
            onChange={reload}
          />
          {/* Only offered when the viewer may actually write here -- the API
              is the enforcement, this just avoids showing a dead end. */}
          {detail.permissions.includes("write") && (
            <Link
              to={`/devices/${id}/edit`}
              style={{
                fontSize: 12,
                letterSpacing: "0.1em",
                color: "var(--text-secondary)",
                border: "1px solid var(--border)",
                padding: "5px 10px",
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              [ EDIT ]
            </Link>
          )}
        </div>
      </div>

      <MaintenanceControl
        variant="banner"
        deviceId={id}
        device={device}
        canWrite={detail.permissions.includes("write")}
        now={now}
        onChange={reload}
      />

      {/* A device owned by another organization. Said plainly, because
          "why can I not edit this" should never be a mystery. */}
      {(device as { is_shared?: boolean }).is_shared && (
        <p
          style={{
            color: "var(--text-secondary)",
            fontSize: 12,
            border: "1px solid var(--border)",
            padding: "8px 12px",
            marginTop: 0,
            marginBottom: 16,
          }}
        >
          {`SHARED WITH YOU BY ${String((device as { owner_name?: string }).owner_name ?? "another organization").toUpperCase()} — READ-ONLY UNLESS THEY GRANTED CONTROL`}
        </p>
      )}

      {/* Only for the organization that owns the device. An organization a
          device is shared with must not be shown a control implying it can
          pass the device on -- the API refuses it either way, but offering
          it and then refusing is worse than not offering it. */}
      {!(device as { is_shared?: boolean }).is_shared && <SharePanel deviceId={id} />}

      {error && (
        <p style={{ color: "var(--status-warning)", fontSize: 12, marginTop: 0 }}>
          {`!! REFRESH FAILED (${error}) -- SHOWING LAST KNOWN DATA`}
        </p>
      )}

      {device.last_poll_error && (
        <div
          style={{
            padding: "8px 12px",
            background: "color-mix(in srgb, var(--status-warning) 15%, var(--surface-1))",
            border: "1px solid var(--status-warning)",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {device.last_poll_error}
        </div>
      )}

      {!wide && discussion}

      {device.protocol === "modbus_tcp" || device.vendor === "morningstar" ? (
        <ErrorBoundary label="CHARGE CONTROLLER">
          <ChargeControllerSection deviceId={id} />
        </ErrorBoundary>
      ) : device.vendor === "tycon" ? (
        <ErrorBoundary label="DC MONITOR">
          <DcMonitorSection deviceId={id} />
        </ErrorBoundary>
      ) : (
        <>
          {/* Nothing at all when the device reports neither: an empty
              section with a range picker and a CSV of blank columns is the
              same dead end as the empty charts were. */}
          {(detail.reports.cpu || detail.reports.temperature) && (
            <ErrorBoundary label="DEVICE HEALTH">
              <RouterDeviceSection deviceId={id} reports={detail.reports} />
            </ErrorBoundary>
          )}
          {hideError && <p style={{ color: "var(--status-critical)", fontSize: 12 }}>{`!! ${hideError}`}</p>}
          {shownInterfaces.map(interfaceChart)}
          {hiddenInterfaces.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 16px", fontSize: 12, color: "var(--text-muted)" }}>
              <span>
                {hiddenInterfaces.length} HIDDEN: {hiddenInterfaces.map((i) => i.if_name).join(", ")}
              </span>
              <button
                onClick={() => setShowHidden((v) => !v)}
                style={{ ...CONTROL_BUTTON, border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)" }}
              >
                {showHidden ? "[ COLLAPSE ]" : "[ SHOW ]"}
              </button>
            </div>
          )}
          {showHidden && hiddenInterfaces.map(interfaceChart)}
        </>
      )}

      <ErrorBoundary label="CUSTOM METRICS">
        <CustomMetricsSection deviceId={id} canWrite={canWrite} />
      </ErrorBoundary>

      {/* The owner's only: the API answers 404 to an organization the device
          is shared with, since a share covers measurements, not who logged
          in where. Not offered rather than offered and refused. */}
      {!(device as { is_shared?: boolean }).is_shared && (
        <ErrorBoundary label="LOGS">
          <DeviceLogsSection deviceId={id} address={device.address} />
        </ErrorBoundary>
      )}
    </div>
    {wide && discussion}
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  padding: 20,
  marginBottom: 16,
};

const sectionTitle: React.CSSProperties = { fontSize: 14, margin: 0, letterSpacing: "0.12em" };
const subLabel: React.CSSProperties = {
  fontSize: 11,
  color: "var(--text-muted)",
  marginBottom: 6,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
};
